/**
 * Client-side identifier minting.
 *
 * ## Why the client is allowed to mint identifiers at all
 *
 * `crypto.randomUUID()` produces a v4 UUID, which is 122 bits of randomness. Two
 * clients generating ids concurrently collide with probability 2^-61, and a
 * server that wants to be certain rejects a duplicate id with a 409 rather than
 * trusting it. So the id is safe to generate on the client, and generating it
 * there buys two things:
 *
 * - An optimistic card insert renders with its final identity immediately. If
 *   the server assigned the id, the local placeholder would have to be renamed
 *   on acknowledgement - which remounts the DOM node, cancels an in-flight drag,
 *   and invalidates any React key derived from it.
 * - The correlation id for a mutation (`opId`) has to be client-generated,
 *   because it exists to match a server echo back to a local pending entry.
 *
 * ## Why `opId` is not a UUID
 *
 * It is never persisted and never leaves the tab that created it, so it needs
 * only to be unique within one client's pending set. A short random suffix is
 * enough, and it keeps frames small - a cursor frame is sent twenty times a
 * second, and an op id rides on every mutation frame.
 *
 * The `op_` prefix exists because the protocol schema enforces a minimum length
 * of 8 characters, and an unprefixed 6-character suffix would fail validation in
 * a way that is easy to miss in review.
 */

export function newOpId(): string {
  const bytes = new Uint8Array(9);
  crypto.getRandomValues(bytes);
  let out = '';
  for (const byte of bytes) out += byte.toString(36).padStart(2, '0');
  return `op_${out}`;
}

/** A per-connection identifier. Regenerated on every socket, and offered back to
 * the server on reconnect as `resumeClientId` so that a brief network blip shows
 * peers a cursor that flickers rather than an avatar that leaves and rejoins. */
export function newClientId(): string {
  return crypto.randomUUID();
}
